import { supabase } from "@/shared/integrations/supabase/client";
import { sameStorageObject } from "@/shared/lib/firebaseStorageUrl";
import type { TablesUpdate } from "@/shared/integrations/supabase/types";

export interface SetAssetPhotoUrlInput {
  /** `asset_documents.id`, or undefined for the synthesised primary entry. */
  photoId?: string;
  /** `property_assets.id` the photo belongs to. */
  assetId: string;
  /** The URL currently stored for this photo. */
  previousUrl: string;
  /** The URL the overwrite minted. */
  nextUrl: string;
}

/**
 * Point every stored reference to a rotated photo at its re-minted URL.
 *
 * Overwriting a Storage object mints a new download token, so the previously
 * stored URL 403s. The object path is unchanged, which is what keeps this safe:
 * `asset_documents` dedupes on `split_part(file_path,'?',1)` (ENG-1034) and the
 * token lives in the query string, so rewriting `file_path` preserves the row's
 * identity and the next Firebase sync still recognises it.
 *
 * Covers the three places a photo's URL can be recorded: the document row, the
 * asset's primary photo column, and its nameplate column.
 */
export async function setAssetPhotoUrl({
  photoId,
  assetId,
  previousUrl,
  nextUrl,
}: SetAssetPhotoUrlInput): Promise<void> {
  if (photoId) {
    const { error } = await supabase
      .from("asset_documents")
      .update({ file_path: nextUrl })
      .eq("id", photoId);
    if (error) throw error;
  }

  const { data, error: readError } = await supabase
    .from("property_assets")
    .select("asset_photo_url, nameplate_photo_url")
    .eq("id", assetId)
    .maybeSingle();
  if (readError) throw readError;

  // Compared by object rather than by string: these columns and the document row
  // routinely hold different tokens for the same object.
  const patch: TablesUpdate<"property_assets"> = {};
  if (sameStorageObject(data?.asset_photo_url, previousUrl)) {
    patch.asset_photo_url = nextUrl;
  }
  if (sameStorageObject(data?.nameplate_photo_url, previousUrl)) {
    patch.nameplate_photo_url = nextUrl;
  }
  if (Object.keys(patch).length === 0) return;

  const { error } = await supabase.from("property_assets").update(patch).eq("id", assetId);
  if (error) throw error;
}
